// Based off of https://github.com/jonathantneal/document-promises
/**
 * The purpose of this class is to supply the client application with a hook where one can
 * begin reading the embedded JSON configurations sent from the server, as well as be notified
 * of when the second flush has completed and has been processed.
 * Once 'document.readyState' === 'interactive' (or 'complete') the JSON configurations embedded in the document
 * used in the various services can be accessed.
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Document/readyState
 * Anything done _before_ the DOMInteractive.ready() promise completes may be done in the first flush, and
 * at that point, the embedded configurations will not be available.
 */
export class DOMInteractiveMarker {
    private static readyPromise: Promise<void>;
    private static shellAndConfigsReadyPromise: Promise<void>;

    public static fullPageReady(): Promise<void> {
        if (DOMInteractiveMarker.readyPromise) {
            return DOMInteractiveMarker.readyPromise;
        }

        // Validate by checking for presence of indicator stipulating that page was rendered in full.
        // This will not be present on the page if we needed to reload the page or redirect elsewhere after first chunk was rendered.
        DOMInteractiveMarker.readyPromise = DOMInteractiveMarker.createListener('FullAuthPageRendered');

        return DOMInteractiveMarker.readyPromise;
    }

    public static shellAndConfigsReady(): Promise<void> {
        if (DOMInteractiveMarker.shellAndConfigsReadyPromise) {
            return DOMInteractiveMarker.shellAndConfigsReadyPromise;
        }

        // Check for indication that shell and config data has loaded
        DOMInteractiveMarker.shellAndConfigsReadyPromise = DOMInteractiveMarker.createListener('ShellInfoLoaded');

        return DOMInteractiveMarker.shellAndConfigsReadyPromise;
    }

    private static createListener(indicatorElementId: string): Promise<void> {
        const eventName = 'readystatechange';

        return new Promise<void>(resolve => {
            const listener = () => {
                if (/^(?:interactive|complete)$/.test(document.readyState)) {
                    document.removeEventListener(eventName, listener);

                    const pageRenderedIndicator = document.getElementById(indicatorElementId);
                    if (pageRenderedIndicator) {
                        resolve();
                    }
                }
            };

            document.addEventListener(eventName, listener);

            listener();
        });
    }
}
